import {array} from '../util';

export const formatDate = (date?: Date | null): string => {
  if (!date) {
    return '';
  }

  const y = String(date.getFullYear());
  let m = String(date.getMonth() + 1);
  let d = String(date.getDate());

  if (m.length < 2) {
    m = '0' + m;
  }

  if (d.length < 2) {
    d = '0' + d;
  }

  return [y, m, d].join('-');
};

export const getTimeText = (date?: Date | null): string => {
  if (!date) {
    return '';
  }

  let h = String(date.getHours());
  let m = String(date.getMinutes());

  if (h.length < 2) {
    h = '0' + h;
  }

  if (m.length < 2) {
    m = '0' + m;
  }

  return [h, m].join(':');
};

const oneDayMilliseconds = 86400000;

export function diffDays(a: Date, b: Date): number {
  return Math.ceil((a.getTime() - b.getTime() + 1) / oneDayMilliseconds);
}

export function formatDateForHuman(date?: Date | null): string {
  if (!date) {
    return '';
  }

  return [
    padLeft(date.getFullYear(), 4),
    padLeft(date.getMonth() + 1, 2),
    padLeft(date.getDate(), 2),
  ].join('/');
}

export function formatDateWithWeekdayForHuman(date?: Date | null): string {
  if (!date) {
    return '';
  }

  const ymd = formatDateForHuman(date);
  const week = Intl.DateTimeFormat('ja-JP', {weekday: 'narrow'}).format(date);
  return ymd + ' (' + week + ')';
}

export function formatDateUTC(date?: Date | null): string {
  if (!date) {
    return '';
  }

  return (
    date.getUTCFullYear() +
    '-' +
    pad(date.getUTCMonth() + 1) +
    '-' +
    pad(date.getUTCDate()) +
    'T' +
    pad(date.getUTCHours()) +
    ':' +
    pad(date.getUTCMinutes()) +
    ':' +
    pad(date.getUTCSeconds()) +
    'Z'
  );
}

export function formatMonthForHuman(date?: Date | null): string {
  if (!date) {
    return '';
  }

  return [padLeft(date.getFullYear(), 4), padLeft(date.getMonth() + 1, 2)].join(
    '/',
  );
}

export function pad(str: string | number): string {
  return padLeft(str, 2);
}

export function padLeft(str: string | number, n: number): string {
  return String(str).padStart(n, '0');
}

export function toDate(str?: string): Date | undefined {
  if (!str || !str.split) {
    return;
  }

  const unixTime = Date.parse(str);

  if (!isNaN(unixTime)) {
    return new Date(unixTime);
  }

  const ymd = str.split('/');

  return new Date(
    parseInt(ymd[0], 10),
    parseInt(ymd[1], 10) - 1,
    parseInt(ymd[2], 10),
  );
}

export function toDates(strs?: string[] | string): Date[] {
  const ss = array(strs);
  return ss.map((s) => toDate(s)).filter((d) => !!d) as Date[];
}

export function formatTime(time: Date): string {
  return pad(time.getHours()) + ':' + pad(time.getMinutes());
}

export function formatBusinessDate(
  date: Date | null,
  businessDayStartTimeMinutes: number = 0,
  delimiter: string = '-',
): [string, string] {
  if (!date) {
    return ['', ''];
  }

  const cutoff = Math.max(0, Math.min(24 * 60, businessDayStartTimeMinutes));

  const h0 = date.getHours();
  const isLate = h0 * 60 + date.getMinutes() < cutoff;
  const base = isLate
    ? new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1)
    : date;

  const y = base.getFullYear();
  const m = base.getMonth();
  const d = base.getDate();
  const h = isLate ? h0 + 24 : h0;
  const mi = date.getMinutes();

  const dateStr = [y, pad(m + 1), pad(d)].join(delimiter);
  const timeStr = `${pad(h)}:${pad(mi)}`;

  return [dateStr, timeStr];
}

export function formatBusinessDateForHuman(
  date: Date | null,
  businessDayStarTimeMinutes?: number,
): [string, string] {
  return formatBusinessDate(date, businessDayStarTimeMinutes, '/');
}

export function parseBusinessDate(
  dateStr: string,
  timeStr: string,
  businessDayStartTimeMinutes: number = 0,
): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hStr, miStr = '0'] = timeStr.split(':');
  const h = Number(hStr);
  const mi = Number(miStr);

  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) {
    throw new Error(`invalid date ${dateStr}`);
  }

  if (!Number.isInteger(h) || !Number.isInteger(mi) || mi < 0 || mi > 59) {
    throw new Error(`invalid time ${timeStr}`);
  }

  if (
    !(h >= 0 && h <= 23) &&
    !(h >= 24 && h * 60 + mi < 24 * 60 + businessDayStartTimeMinutes)
  ) {
    throw new Error(
      `hour:minute must be 00:00–23:59 or 24:00–${
        23 + Math.floor(businessDayStartTimeMinutes / 60)
      }:${businessDayStartTimeMinutes % 60}`,
    );
  }

  return new Date(y, m - 1, d, h, mi, 0, 0);
}

export function addMinutesToDate(
  date: Date | null | undefined,
  minutes: number = 0,
): Date | null {
  if (!date) {
    return null;
  }

  if (minutes === 0) {
    return date;
  }

  return new Date(date.getTime() + minutes * 60 * 1000);
}

export const calendarStringsJa = {
  months: [
    '1月',
    '2月',
    '3月',
    '4月',
    '5月',
    '6月',
    '7月',
    '8月',
    '9月',
    '10月',
    '11月',
    '12月',
  ],
  shortMonths: [
    '1月',
    '2月',
    '3月',
    '4月',
    '5月',
    '6月',
    '7月',
    '8月',
    '9月',
    '10月',
    '11月',
    '12月',
  ],
  days: ['日曜', '月曜', '火曜', '水曜', '木曜', '金曜', '土曜'],
  shortDays: ['日', '月', '火', '水', '木', '金', '土'],
  goToToday: '今日',
  prevMonthAriaLabel: '先月',
  nextMonthAriaLabel: '次月',
  prevYearAriaLabel: '前年',
  nextYearAriaLabel: '翌年',
  closeButtonAriaLabel: '閉じる',
};
